import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiBaseQuery, Methods } from '@app/api/shared';
import {
  WorkScheduleEndpoints as Endpoints,
  type CheckEmployeeWorkBreakRequest,
  type CheckEmployeeWorkBreakResponse,
  type CheckEmployeeWorkScheduleRequest,
  type CheckEmployeeWorkScheduleResponse,
  type DeleteEmployeeWorkScheduleForDateRequest,
  type DeleteEmployeeWorkScheduleForDateResponse,
  type DeleteWorkBreaksRequest,
  type DeleteWorkBreaksResponse,
  type DeleteWorkSchedulesByPeriodRequest,
  type DeleteWorkSchedulesByPeriodResponse,
  type DeleteWorkSchedulesRequest,
  type DeleteWorkSchedulesResponse,
  type GetEmployeeWorkScheduleForDateRequest,
  type GetEmployeeWorkScheduleForDateResponse,
  type GetEmployeeWorkSchedulesRequest,
  type GetEmployeeWorkSchedulesResponse,
  type GetEmployeesWithoutWorkScheduleRequest,
  type GetEmployeesWithoutWorkScheduleResponse,
  type GetEmployeesWorkSchedulesByUserIdsRequest,
  type GetEmployeesWorkSchedulesByUserIdsResponse,
  type GetEmployeesWorkSchedulesRequest,
  type GetEmployeesWorkSchedulesResponse,
  type GetWorkScheduleByIdRequest,
  type GetWorkScheduleByIdResponse,
  type GetWorkScheduleByIdsRequest,
  type GetWorkScheduleByIdsResponse,
  type UpdateWorkBreakRequest,
  type UpdateWorkBreakResponse,
  type UpdateWorkScheduleRequest,
  type UpdateWorkScheduleResponse,
  type UpdateWorkSchedulesRequest,
  type UpdateWorkSchedulesResponse,
} from '@app/api/WorkSchedule/types';
import { WORK_SCHEDULE } from '@app/constants';

export const workScheduleApi2 = createApi({
  reducerPath: 'workScheduleApi2',
  baseQuery: createApiBaseQuery(WORK_SCHEDULE),
  endpoints: ({ mutation, query }) => ({
    [Endpoints.UpdateWorkSchedule]: mutation<
      UpdateWorkScheduleResponse,
      UpdateWorkScheduleRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateWorkSchedule,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.UpdateWorkBreak]: mutation<
      UpdateWorkBreakResponse,
      UpdateWorkBreakRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateWorkBreak,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.UpdateWorkSchedules]: mutation<
      UpdateWorkSchedulesResponse,
      UpdateWorkSchedulesRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateWorkSchedules,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.DeleteWorkSchedules]: mutation<
      DeleteWorkSchedulesResponse,
      DeleteWorkSchedulesRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.DeleteWorkSchedules,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.DeleteWorkBreaks]: mutation<
      DeleteWorkBreaksResponse,
      DeleteWorkBreaksRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.DeleteWorkBreaks,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.DeleteWorkSchedulesByPeriod]: mutation<
      DeleteWorkSchedulesByPeriodResponse,
      DeleteWorkSchedulesByPeriodRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.DeleteWorkSchedulesByPeriod,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetWorkScheduleById]: query<
      GetWorkScheduleByIdResponse,
      GetWorkScheduleByIdRequest
    >({
      query: (params) => ({
        url: Endpoints.GetWorkScheduleById,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetEmployeeWorkSchedules]: mutation<
      GetEmployeeWorkSchedulesResponse,
      GetEmployeeWorkSchedulesRequest
    >({
      query: (params) => ({
        url: Endpoints.GetEmployeeWorkSchedules,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.GetEmployeesWorkSchedules]: mutation<
      GetEmployeesWorkSchedulesResponse,
      GetEmployeesWorkSchedulesRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.GetEmployeesWorkSchedules,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetEmployeesWorkSchedulesByUserIds]: mutation<
      GetEmployeesWorkSchedulesByUserIdsResponse,
      GetEmployeesWorkSchedulesByUserIdsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.GetEmployeesWorkSchedulesByUserIds,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetWorkScheduleByIds]: mutation<
      GetWorkScheduleByIdsResponse,
      GetWorkScheduleByIdsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.GetWorkScheduleByIds,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetEmployeesWithoutWorkSchedule]: query<
      GetEmployeesWithoutWorkScheduleResponse,
      GetEmployeesWithoutWorkScheduleRequest
    >({
      query: (params) => ({
        url: Endpoints.GetEmployeesWithoutWorkSchedule,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetEmployeeWorkScheduleForDate]: query<
      GetEmployeeWorkScheduleForDateResponse,
      GetEmployeeWorkScheduleForDateRequest
    >({
      query: (params) => ({
        url: Endpoints.GetEmployeeWorkScheduleForDate,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.CheckEmployeeWorkSchedule]: query<
      CheckEmployeeWorkScheduleResponse,
      CheckEmployeeWorkScheduleRequest
    >({
      query: (params) => ({
        url: Endpoints.CheckEmployeeWorkSchedule,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.CheckEmployeeWorkBreak]: query<
      CheckEmployeeWorkBreakResponse,
      CheckEmployeeWorkBreakRequest
    >({
      query: (params) => ({
        url: Endpoints.CheckEmployeeWorkBreak,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.DeleteEmployeeWorkScheduleForDate]: mutation<
      DeleteEmployeeWorkScheduleForDateResponse,
      DeleteEmployeeWorkScheduleForDateRequest
    >({
      query: (params) => ({
        url: Endpoints.DeleteEmployeeWorkScheduleForDate,
        method: Methods.POST,
        params,
      }),
    }),
  }),
});

export const {
  useUpdateWorkScheduleMutation,
  useUpdateWorkBreakMutation,
  useUpdateWorkSchedulesMutation,
  useDeleteWorkSchedulesMutation,
  useDeleteWorkBreaksMutation,
  useDeleteWorkSchedulesByPeriodMutation,
  useGetWorkScheduleByIdQuery,
  useLazyGetWorkScheduleByIdQuery,
  useGetEmployeeWorkSchedulesMutation,
  useGetEmployeesWorkSchedulesMutation,
  useGetEmployeesWorkSchedulesByUserIdsMutation,
  useGetWorkScheduleByIdsMutation,
  useGetEmployeesWithoutWorkScheduleQuery,
  useLazyGetEmployeesWithoutWorkScheduleQuery,
  useGetEmployeeWorkScheduleForDateQuery,
  useLazyGetEmployeeWorkScheduleForDateQuery,
  useCheckEmployeeWorkScheduleQuery,
  useLazyCheckEmployeeWorkScheduleQuery,
  useCheckEmployeeWorkBreakQuery,
  useLazyCheckEmployeeWorkBreakQuery,
  useDeleteEmployeeWorkScheduleForDateMutation,
} = workScheduleApi2;
